import React from 'react';
import { theme } from '../theme';

/**
 * MomentumBar — rebuilt as a slim status strip (Sep 2, per Ali's
 * report). First rebuilt as a full card matching the streak/referral
 * visual language, then revised again the same night after design
 * review: a full card gave this the same visual weight as streak and
 * referral, which both grant something real (a bonus pin) — this
 * grants nothing by design (see below), so competing for the same
 * space read as more important than it actually is. This single-row
 * strip keeps the same navy/white/square-corner language but takes a
 * fraction of the vertical space, sitting between the Welcome back
 * card and the streak/referral row rather than stacking a third full
 * card above them.
 *
 * Deliberately no reward tied to reaching the top tier (Sep 2, per
 * Ali's report, after discussing whether to attach a bonus pin at
 * Deal Master) — this stays pure status/gamification, unlike the
 * streak and referral cards, which are the app's only two mechanics
 * that actually grant something. That's a real, acknowledged
 * asymmetry, not an oversight.
 *
 * Level thresholds still map onto the 6 real, data-derived achievement
 * badges (see growth.py's _compute_achievements) — every 2 earned
 * badges is a new level, so all 6 possible badges land cleanly inside
 * a defined band with no leftover count.
 */
export const LEVELS = [
  { threshold: 0, label: 'Getting started', icon: 'seedling' },
  { threshold: 2, label: 'Deal scout', icon: 'search' },
  { threshold: 4, label: 'Deal hunter', icon: 'target' },
  { threshold: 6, label: 'Deal master', icon: 'trophy' },
];

// Extracted (Aug 16/17, per Ali's report) so anything else needing the
// current tier computes it the exact same way as this card, from one
// source of truth. Returns { currentLevel, nextLevel, pct } — pct is
// 100 once there's no next level (all badges earned).
export function computeCurrentLevel(achievements) {
  if (!achievements?.achievements?.length) return null;

  const earned = achievements.earned_count || 0;
  const total = achievements.total_count || LEVELS[LEVELS.length - 1].threshold;

  let currentLevel = LEVELS[0];
  let nextLevel = null;
  for (let i = 0; i < LEVELS.length; i++) {
    if (earned >= LEVELS[i].threshold) {
      currentLevel = LEVELS[i];
      nextLevel = LEVELS[i + 1] || null;
    }
  }

  const bandStart = currentLevel.threshold;
  const bandEnd = nextLevel ? nextLevel.threshold : total;
  const bandWidth = (bandEnd - bandStart) || 1;
  const pct = nextLevel ? Math.min(100, ((earned - bandStart) / bandWidth) * 100) : 100;

  return { currentLevel, nextLevel, pct, earned, total };
}

// Simple inline stroke-icons (Sep 2, per Ali's report) — replacing the
// original 🌱🔍🎯🏆 emoji. Not a blanket policy change (plenty of
// emoji were deliberately kept elsewhere in tonight's cleanup pass,
// this system's own icons included) — specifically upgraded here
// because rebuilding this card from scratch was the natural moment to
// match the same clean line-icon treatment used in the approved
// mockup, rather than leaving the two visually inconsistent.
export function TierIcon({ type, color }) {
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (type === 'seedling') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M12 21v-9" />
        <path d="M12 12C12 7 8 5 4 5c0 5 3 8 8 8" />
        <path d="M12 12c0-4 3-6 7-6 0 4-2 7-7 7" />
      </svg>
    );
  }
  if (type === 'search') {
    return (
      <svg {...common} aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="M20 20l-4.8-4.8" />
      </svg>
    );
  }
  if (type === 'target') {
    return (
      <svg {...common} aria-hidden="true">
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="12" cy="12" r="0.5" fill={color} />
      </svg>
    );
  }
  // trophy
  return (
    <svg {...common} aria-hidden="true">
      <path d="M8 4h8v6a4 4 0 0 1-8 0V4z" />
      <path d="M8 5H5a2 2 0 0 0 2 4" />
      <path d="M16 5h3a2 2 0 0 1-2 4" />
      <path d="M10 16h4v3h-4z" />
      <path d="M8 21h8" />
    </svg>
  );
}

export default function MomentumBar({ achievements }) {
  const result = computeCurrentLevel(achievements);
  if (!result) return null;
  const { currentLevel, nextLevel, earned } = result;

  return (
    <div style={{
      background: theme.surface, border: `2px solid ${theme.border}`, borderRadius: '0px',
      padding: '12px 16px', marginBottom: '14px',
      display: 'flex', alignItems: 'center', gap: '12px'
    }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%', background: theme.structural,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        <TierIcon type={currentLevel.icon} color="#fff" />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: theme.textPrimary }}>{currentLevel.label}</div>
        <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '1px' }}>
          {nextLevel
            ? `${nextLevel.threshold - earned} more badge${nextLevel.threshold - earned === 1 ? '' : 's'} to ${nextLevel.label}`
            : "You've reached the top tier"}
        </div>
      </div>

      {/* 4 segments track TIER progress (Getting started -> Deal
          master), not the 6 underlying badges directly — matches the
          streak card's own dot-per-unit simplicity rather than trying
          to show all 6 badges individually in a space this compact. */}
      <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
        {LEVELS.map((level, i) => (
          <div
            key={level.label}
            style={{
              width: '14px', height: '4px',
              background: earned >= level.threshold ? theme.structural : theme.divider
            }}
          />
        ))}
      </div>
    </div>
  );
}